import {
    ChangeDetectionStrategy,
    Component,
    OnInit,
} from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    Validators,
} from '@angular/forms';
import { UiModalComponent } from '@components/ui-modal/ui-modal.component';
import {
    IMachine,
    MachineType,
    ModalResultType,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import { MachineService } from '@services';
import UiValidators, { UI_MACHINE_NAME_MAX_LENGTH } from '@validators';

import { BehaviorSubject } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Component({
    templateUrl: './machine.modal.html',
    styleUrls: ['machine.modal.scss'],
    providers: [FormBuilder, MachineService],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MachineModalComponent
    extends UiModalComponent<IMachine>
    implements OnInit {

    public form!: FormGroup;
    public loading$ = new BehaviorSubject(false);

    public isLicenseKeyReadonly = true;
    public isNameReadonly!: boolean;
    public isStandardMachine!: boolean;
    public modalTitle!: string;

    public MachineType = MachineType;

    private _machineService = this._injector.get<MachineService>(MachineService);
    private _translateService = this._injector.get<TranslateService>(TranslateService);

    ngOnInit() {
        const type = this.context.additionalData.type;
        this.context.data.type = MachineType[type] as keyof typeof MachineType;

        this.isStandardMachine = type === MachineType.Standard;
        this.isNameReadonly = this.isStandardMachine && !this.context.isNewEntry;

        const titleKey =
            this.context.isNewEntry ?
                this.isStandardMachine ?
                    'machine_modal_add_standard_title' :
                    'machine_modal_add_template_title' :
                this.isStandardMachine ?
                    'machine_modal_edit_standard_title' :
                    'machine_modal_edit_template_title';
        this.modalTitle = this._translateService.instant(titleKey, {
            machineName: this.context.data.name,
        });


        this.form = this._injector.get<FormBuilder>(FormBuilder).group({
            'name': [
                this.context.data.name,
                Validators.compose([
                    UiValidators.required(),
                    Validators.maxLength(UI_MACHINE_NAME_MAX_LENGTH),
                ])],
            'licenseKey': [
                this.context.data.licenseKey,
            ],
            'description': [
                this.context.data.description,
                Validators.maxLength(500),
            ],
        });
    }

    public clipboardCopied() {
        this._snack.success(this._translateService.instant('global_message_copied_clipboard'));
    }

    public onSubmit() {
        if (
            !this.form.valid ||
            this.loading$.value
        ) {
            return;
        }

        this.loading$.next(true);

        const formValue = this.form.value;
        this.context.data.licenseKey = formValue.licenseKey;
        this.context.data.name = formValue.name;
        this.context.data.description = formValue.description;

        if (this.context.isNewEntry) {
            this._machineService.add(this.context.data)
                .pipe(
                    finalize(
                        () => this.loading$.next(false),
                    ),
                )
                .subscribe(
                    () =>
                        this._dialogRef.close({
                            type: ModalResultType.Add,
                        }),
                );
        } else {
            this._machineService.update(this.context.data.id, this.context.data)
                .pipe(
                    finalize(
                        () => this.loading$.next(false),
                    ),
                )
                .subscribe(
                    () =>
                        this._dialogRef.close({
                            type: ModalResultType.Update,
                        }),
                );
        }
    }
}
